import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next'; // 🎯 الترجمة
import { Link2, Save, Settings, Table as TableIcon } from 'lucide-react';

const FieldConfig = () => {
    const { t, i18n } = useTranslation();
    const [config, setConfig] = useState({ table_name: 'organizations', field_name: '', domain_id: '' });
    const [domains, setDomains] = useState([]);
    const [existingMaps, setExistingMaps] = useState([]);

    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

    const fetchMaps = () => {
        axios.get(`/api/admin/field-map/${config.table_name}`)
            .then(res => setExistingMaps(Object.entries(res.data)))
            .catch(() => setExistingMaps([]));
    };

    useEffect(() => {
        // 🎯 التصحيح هنا: الدومينات دلوقتي بترجع كـ Object فيه data
        axios.get('/api/domains?limit=1000')
            .then(res => {
                const data = res.data.data || res.data; // دعم الشكل الجديد والقديم
                setDomains(Array.isArray(data) ? data : []);
            })
            .catch(() => setDomains([]));
            
        fetchMaps();
    }, [config.table_name]);

    const handleLink = async () => {
        if (!config.field_name || !config.domain_id) return toast.error(t('importer.toasts.no_file')); // استخدام مفتاح قريب
        const loadId = toast.loading(t('workflow.toasts.saving'));
        try {
            await axios.post('/api/admin/field-map', config);
            toast.success(t('workflow.toasts.save_success'), { id: loadId });
            fetchMaps();
        } catch (err) {
            toast.error(t('workflow.toasts.save_fail'), { id: loadId });
        }
    };

    return (
        <div className={`p-6 bg-[#f3f4f6] min-h-screen text-[12px] ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'}`}>
            <div className="max-w-4xl mx-auto space-y-4">
                <div className="bg-white p-6 border shadow-sm">
                    <h2 className={`text-[#1a3353] font-bold mb-6 flex items-center gap-2 text-sm uppercase `}>
                        <Settings size={20}/> {t('field_config.title')}
                    </h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="flex flex-col">
                            <label className="font-bold text-gray-500 mb-1">{t('field_config.table_name')}</label>
                            <select className={`border p-2 outline-none ${isRTL ? 'text-right' : 'text-left'}`} value={config.table_name}
                                    onChange={e => setConfig({...config, table_name: e.target.value})}>
                                <option value="organizations">Organizations</option>
                                <option value="sites">Sites</option>
                                <option value="assets">Assets</option>
                                <option value="items">Items</option>      
                                <option value="tools">Tools</option>                                
                                <option value="workorders">Workorders</option>
                                <option value="AssetMeters">AssetMeters</option>
                                <option value="Meters">Meters</option>                                 
                                <option value="Locations">Locations</option>
                                <option value="PM">PM</option>
                                <option value="CM">CM</option>
                                <option value="jobplans">Jobplans</option>
                                <option value="companies">Companies</option>                      
                            </select>
                        </div>
                        <div className="flex flex-col">
                            <label className="font-bold text-gray-500 mb-1">{t('field_config.field_name')}</label>
                            <input className={`border p-2 outline-none uppercase font-bold text-blue-900 ${isRTL ? 'text-right' : 'text-left'}`} 
                                   placeholder="e.g. STATUS"
                                   value={config.field_name}
                                   onChange={e => setConfig({...config, field_name: e.target.value.toLowerCase()})} />
                        </div>
                        <div className="flex flex-col">
                            <label className="font-bold text-gray-500 mb-1">{t('domains.domain_id')}</label>
                            <select className={`border p-2 outline-none font-bold text-blue-800 ${isRTL ? 'text-right' : 'text-left'}`}
                                    value={config.domain_id}
                                    onChange={e => setConfig({...config, domain_id: e.target.value})}>
                                <option value="">-- {t('workflow.options.select_status')} --</option>
                                {domains.map(d => <option key={d.domain_id} value={d.domain_id}>{d.domain_id}</option>)}
                            </select>
                        </div>
                    </div>
                    
                    <div className={`mt-6 flex ${isRTL ? 'justify-end' : 'justify-start'}`}>
                        <button onClick={handleLink} className="bg-[#3c70a4] text-white px-8 py-2 font-bold hover:bg-[#1a3353] flex items-center gap-2 shadow-md transition-all">
                            <Link2 size={16}/> {t('sidebar.save')}
                        </button>
                    </div>
                </div>

                {/* عرض الربط الحالي */}
                <div className="bg-white border shadow-sm">
                    <div className={`p-3 bg-gray-50 border-b font-bold text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                        {t('field_config.current_mappings')} {config.table_name.toUpperCase()}
                    </div>
                    <table className="w-full">
                        <thead className="bg-gray-100 text-gray-500">
                            <tr className={isRTL ? 'flex-row-reverse' : ''}>
                                <th className={`p-2 border-r ${isRTL ? 'text-right' : 'text-left'}`}>{t('field_config.field')}</th>
                                <th className={`p-2 ${isRTL ? 'text-right' : 'text-left'}`}>{t('field_config.linked_domain')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {existingMaps.length > 0 ? existingMaps.map(([field, domain]) => (
                                <tr key={field} className={`border-b ${isRTL ? 'flex-row-reverse' : ''}`}>
                                    <td className={`p-2 border-r font-bold text-blue-900 ${isRTL ? 'text-right' : 'text-left'}`}>{field}</td>
                                    <td className={`p-2 text-gray-600 ${isRTL ? 'text-right' : 'text-left'}`}>{domain}</td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="2" className="p-4 text-center text-gray-400 italic">{t('dashboard.no_data')}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default FieldConfig;